import React, { useMemo, useState } from 'react'
import {
  DetailCard,
  DetailCardGrid,
  NavList,
  type DetailCardRow,
} from '@enamelai/design-system'
import './NetworkPlansStyle.css'

interface AmountDetail {
  amount: number
  remaining: number
  used: number
}

interface NetworkService {
  category: string
  deductibles?: {
    family?: AmountDetail
    individual?: AmountDetail
  }
  maximum?: {
    family?: AmountDetail
    individual?: AmountDetail
  }
}

export interface NetworkPlan {
  network: string
  networkType: number
  services: NetworkService[]
}

interface PlanCard {
  title: string
  rows: DetailCardRow[]
}

interface CategoryLimit {
  title: string
  individual: PlanCard
  family: PlanCard
}

export interface NetworkPlansProps {
  className?: string
  data: NetworkPlan[]
  initialNetwork?: string
  planSubtitle?: string
}

const DENTAL_CARE = 'Dental Care'

const currency = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })

const rangeValue = (detail: AmountDetail): string =>
  `${currency(detail.used)} / ${currency(detail.amount)}`

const hasData = (service: NetworkService): boolean =>
  Boolean(service.deductibles || service.maximum)

const buildOverview = (
  service?: NetworkService,
): { individual: PlanCard; family: PlanCard } => {
  const individualRows: DetailCardRow[] = []
  const familyRows: DetailCardRow[] = []

  const dedInd = service?.deductibles?.individual
  if (dedInd) {
    individualRows.push({
      label: 'Deductible',
      value: currency(dedInd.amount),
      met: dedInd.remaining === 0,
    })
  }

  const maxInd = service?.maximum?.individual
  if (maxInd) {
    individualRows.push({ label: 'Annual maximum', value: rangeValue(maxInd) })
    individualRows.push({
      label: 'Remaining',
      value: currency(maxInd.remaining),
      highlight: true,
    })
  }

  const dedFam = service?.deductibles?.family
  if (dedFam) {
    familyRows.push({
      label: 'Deductible',
      value: currency(dedFam.amount),
      met: dedFam.remaining === 0,
    })
  }

  const maxFam = service?.maximum?.family
  familyRows.push(
    maxFam
      ? { label: 'Annual maximum', value: rangeValue(maxFam) }
      : { label: 'Annual maximum', value: 'None', muted: true },
  )

  return {
    individual: { title: 'Individual', rows: individualRows },
    family: { title: 'Family', rows: familyRows },
  }
}

const buildCategory = (service: NetworkService): CategoryLimit => {
  const individualRows: DetailCardRow[] = []
  const familyRows: DetailCardRow[] = []

  const maxInd = service.maximum?.individual
  if (maxInd) {
    individualRows.push({
      label: 'Lifetime maximum',
      value: rangeValue(maxInd),
    })
    individualRows.push({
      label: 'Remaining',
      value: currency(maxInd.remaining),
      highlight: true,
    })
  }

  const maxFam = service.maximum?.family
  familyRows.push(
    maxFam
      ? { label: 'Lifetime maximum', value: rangeValue(maxFam) }
      : { label: 'Lifetime maximum', value: 'None', muted: true },
  )

  return {
    title: service.category,
    individual: { title: 'Individual', rows: individualRows },
    family: { title: 'Family', rows: familyRows },
  }
}

export const NetworkPlans: React.FC<NetworkPlansProps> = ({
  className,
  data,
  initialNetwork,
  planSubtitle = 'Deductible & annual maximum - applies to all services except those with their own limit below.',
}) => {
  const [activeNetwork, setActiveNetwork] = useState<string>(
    initialNetwork ?? data[0]?.network ?? '',
  )

  const activePlan = useMemo(
    () => data.find((p) => p.network === activeNetwork) ?? data[0],
    [data, activeNetwork],
  )

  const networkItems = useMemo(
    () => data.map((plan) => ({ id: plan.network, label: plan.network })),
    [data],
  )

  const { overview, categories, planTag } = useMemo(() => {
    if (!activePlan) {
      return { overview: buildOverview(undefined), categories: [], planTag: '' }
    }

    const dentalCare = activePlan.services.find(
      (s) => s.category === DENTAL_CARE,
    )

    const categoryLimits = activePlan.services
      .filter((s) => s.category !== DENTAL_CARE && hasData(s))
      .map(buildCategory)

    return {
      overview: buildOverview(dentalCare),
      categories: categoryLimits,
      planTag: activePlan.networkType === 0 ? 'In-network' : 'Out-of-network',
    }
  }, [activePlan])

  return (
    <div className={`network-plans${className ? ` ${className}` : ''}`}>
      <NavList
        className="np-sidebar"
        title="Networks"
        items={networkItems}
        value={activePlan?.network}
        onChange={setActiveNetwork}
      />

      <section className="np-main">
        <h3 className="np-main__title">Plan overall</h3>

        <div className="np-panel">
          <header className="np-panel__header">
            <div className="np-panel__heading-group">
              <h4 className="np-panel__heading">{activePlan?.network}</h4>
              <p className="np-panel__subtitle">{planSubtitle}</p>
            </div>
            <span className="np-panel__tag">{planTag}</span>
          </header>

          <DetailCardGrid>
            <DetailCard
              title={overview.individual.title}
              rows={overview.individual.rows}
            />
            <DetailCard
              title={overview.family.title}
              rows={overview.family.rows}
            />
          </DetailCardGrid>

          {categories.length > 0 && (
            <>
              <h5 className="np-section-title">Category-specific limits</h5>
              {categories.map((cat) => (
                <div className="np-category" key={cat.title}>
                  <div className="np-category__name">{cat.title}</div>
                  <DetailCardGrid>
                    <DetailCard
                      variant="tinted"
                      title={cat.individual.title}
                      rows={cat.individual.rows}
                    />
                    <DetailCard
                      variant="tinted"
                      title={cat.family.title}
                      rows={cat.family.rows}
                    />
                  </DetailCardGrid>
                </div>
              ))}
            </>
          )}
        </div>
      </section>
    </div>
  )
}

export default NetworkPlans
